import React, { useCallback, useEffect, useState } from 'react';
import { ILocation } from '../../type/location';
import useLocationStore from '../../store/locatonStore';
import ReusableSelect from '../../components/common/Select';
import operationAPI from '../../api/operationApi';
import DateSelector from '../../components/common/calendar/dateSelector';
import SunIcon from '../../assets/SunIcon';
import BusCard from '../../components/common/BusCard';
import { IOperationalBusDetail } from '../../interfaces/operationBusDetail';
import {
  IBusPickupAllocationDto,
  IPaxDriverAllocation,
} from '../../interfaces/driverPaxAllocation';
import { encodeDateForUrl, getAdjustedDate } from '../../utils/dateUtils';
import driverAllocationAPI from '../../api/driverAllocationAPI';
import ReusableButton from '../../components/common/Button/Button';
import EditIcon from '../../assets/EditIcon';
import AccordianDownIcon from '../../assets/AccordianDownIcon';
import ReusableIconButton from '../../components/common/Button/ReusableIconButton';
import CrossIcon from '../../assets/CrossIcon';
import CheckIcon from '../../assets/CheckIcon';
import Loader from '../../assets/loader/Loader';
import { translate } from '../../i18n';
import {
  dropOffHeader,
  dropOffHeaderKeyMapping,
} from '../../constants/headerConstants';
import {
  allocation,
  defaultHiddenDropOffColumns,
  locationConstant,
  truncateColumn,
} from '../../constants/constant';
import { toast } from 'react-toastify';
import { ITableHeader } from '../../interfaces/tableHeader';
import { IVisibleColumns } from '../../interfaces/visibleColumns';
import { AllocationsPax } from '../../components';
import useTemperatureStore from '../../store/temperatureStore';
import useDateStore from '../../store/selectedDateStore';
import { ISelectedBusData } from '../../interfaces/selectedBusData';
import { IOriginalAllocation } from '../../interfaces/originalBusAllocation';
import useBgColorStore from '../../store/bgColor';
import CalendarHeader from '../../components/common/Header/CalenderHeader';
import SunIconDark from '../../assets/SunIconDark';
import AccordianDownIconDark from '../../assets/AccordianDownIconDark';
import EditIconDark from '../../assets/EditIconDark';
import { showOrReplaceToast } from '../../utils/utility';
import { useClickOutside } from '../../hooks/useClickOutside';
import ReusableSelectLocation from '../../components/common/SelectLocation';

const DropOffDesktop = () => {
  const { bgColor, updateBgColor } = useBgColorStore();
  const { selectedDateFromStore, setSelectedDateFromStore } = useDateStore();
  const [loading, setLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [operationBus, setOperationBus] = useState<IOperationalBusDetail[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [headers, setHeaders] = useState<ITableHeader[]>(dropOffHeader);

  const keyMapping: Record<string, string> = dropOffHeaderKeyMapping;

  const defaultVisibleColumns = headers.reduce<IVisibleColumns>(
    (acc, header) => {
      const key = header.label.replace(/\s+/g, '').toLowerCase();
      acc[key] = !defaultHiddenDropOffColumns.includes(key);
      return acc;
    },
    {},
  );

  const [visibleColumns, setVisibleColumns] = useState<IVisibleColumns>(
    defaultVisibleColumns,
  );
  const [isToggleColumns, setIsToggleColumns] = useState<boolean>(false);

  const [locationList, setLocationList] = useState<ILocation[]>();
  const [selectedDate, setSelectedDate] = useState<Date>(
    selectedDateFromStore || getAdjustedDate(),
  );

  const [realTimeBusPax, setRealTimeBusPax] = useState<{
    [key: string]: number;
  }>({
    totalPax: 0,
    totalAllocatedPax: 0,
  });
  const [busAllocations, setBusAllocations] = useState<{
    [operationStaffId: string]: number;
  }>({});

  const [bookings, setBookings] = useState<IPaxDriverAllocation[]>();
  const [busPax, setBusPax] = useState<{
    [key: string]: number;
  }>({
    totalPax: 0,
    totalAllocatedPax: 0,
  });

  const [selectedBuses, setSelectedBuses] = useState<{
    [key: string]: ISelectedBusData;
  }>({});

  const [originalAllocations, setOriginalAllocations] = useState<{
    [bookingId: string]: IOriginalAllocation[];
  }>({});

  const [unallocatedPax, setUnallocatedPax] = useState<
    IPaxDriverAllocation[] | null
  >();
  const { selectedTemperature } = useTemperatureStore();
  const selectedLocation = useLocationStore((state) => state.selectedLocation);
  const [selectedLocationId, setSelectedLocationId] = useState<string | number>(
    selectedLocation?.OwnerId,
  );
  const setSelectedLocation = useLocationStore(
    (state) => state.setSelectedLocation,
  );

  const [paxErrors, setPaxErrors] = useState<Record<string, boolean>>({});

  const handleLocationChange = (selectedLocation: ILocation) => {
    setSelectedLocationId(selectedLocation.OwnerId);
    setSelectedLocation(selectedLocation);
    updateBgColor();
  };

  const fetchOwnerLocations = useCallback(async () => {
    try {
      const response = await operationAPI.getLocationList();
      setLocationList(response?.data);
    } catch (error) {
      console.error(error);
      // add alert message
    }
  }, []);

  const onSetUnallocatedPax = useCallback(
    (bookings: IPaxDriverAllocation[]) => {
      const unallocated = bookings?.filter((booking: IPaxDriverAllocation) => {
        return booking?.busPickupAllocation?.length === 0;
      });

      setUnallocatedPax(unallocated);
    },
    [], // Dependencies: none, as it only uses `setUnallocatedPax`, which is stable
  );

  const getPickupAllocationDetails = useCallback(
    async (date: Date, ownerId: number, isShowLoader?: boolean) => {
      const encodedDate = encodeDateForUrl(date);
      try {
        setIsPageLoading(isShowLoader ?? false);
        const response = await driverAllocationAPI.getDriverDropOffAllocation(
          ownerId,
          encodedDate,
        );
        console.log(response?.data);

        setOperationBus(response?.data?.operationDetails?.busDetails);
        setBookings(response?.data?.bookings);
        setBusPax({
          totalPax: response?.data?.totalPax ?? 0,
          totalAllocatedPax:
            response?.data?.operationDetails?.totalAllocatedPax ?? 0,
        });
        onSetUnallocatedPax(response?.data?.bookings);
        // Initialize selectedBuses and originalAllocations
        const initialSelectedBuses: { [key: string]: ISelectedBusData } = {};
        const initialOriginalAllocations: {
          [key: string]: IOriginalAllocation[];
        } = {};

        response?.data.bookings?.forEach((booking: IPaxDriverAllocation) => {
          const bookingIdStr = String(booking.bookingId);
          const zoneIdStr = String(booking.ZoneId);
          const bookingProductTripIdStr = String(booking.bookingProductTripId);
          const combinedKey = `${bookingIdStr}_${zoneIdStr}_${bookingProductTripIdStr}`;

          // Initialize arrays if needed
          if (!initialOriginalAllocations[combinedKey]) {
            initialOriginalAllocations[combinedKey] = [];
          }

          if (!initialSelectedBuses[combinedKey]) {
            initialSelectedBuses[combinedKey] = {
              bookingProductTripPickupId: booking.bookingProductTripDropOffId,
              bookingId: booking.bookingId,
              paxNo: booking.paxNo,
              allocNo: booking.allocPax,
              zoneId: booking.ZoneId,
              allocations: [],
            };
          }

          // Handle array of allocations
          booking.busPickupAllocation?.forEach(
            (allocation: IBusPickupAllocationDto) => {
              if (allocation.operationStaffId) {
                // Add to original allocations
                initialOriginalAllocations[combinedKey].push({
                  operationStaffId: allocation.operationStaffId,
                  busAllocationId: allocation?.busAllocationId,
                  paxNo: Number(allocation.paxNo || booking.allocPax),
                });

                // Add to selected buses
                initialSelectedBuses[combinedKey].allocations.push({
                  operationStaffId: allocation.operationStaffId,
                  busAllocationId: allocation?.busAllocationId,
                  oldOperationStaffId: allocation.operationStaffId,
                  splitPax: Number(allocation.paxNo || booking.allocPax),
                  isDeleted: false,
                });
              }
            },
          );

          // If no allocations were found, remove the entry
          if (initialSelectedBuses[combinedKey].allocations.length === 0) {
            delete initialSelectedBuses[combinedKey];
          }
        });
        // set value
        setOriginalAllocations(initialOriginalAllocations);
        setSelectedBuses(initialSelectedBuses);
        setPaxErrors({});
      } catch (error) {
        console.error('Error fetching allocation details:', error);
      } finally {
        setIsPageLoading(false);
      }
    },
    [],
  );
  useEffect(() => {
    setSelectedDate(selectedDateFromStore);
  }, [selectedDateFromStore]);

  useEffect(() => {
    fetchOwnerLocations();
  }, [fetchOwnerLocations]);

  useEffect(() => {
    getPickupAllocationDetails(selectedDate, selectedLocation.OwnerId, true);
  }, [selectedDate, getPickupAllocationDetails, selectedLocation]);

 useEffect(() => {
   if (operationBus && operationBus?.length > 0) {
     const busHeaders = operationBus?.map((bus) => ({
       label: bus?.initials,
       key: bus?.operationStaffId?.toString(),
       title: bus?.busName,
     }));

     const staticHeaders = headers?.filter((header) => !header.key) || [];
     const newHeaders = [...staticHeaders, ...busHeaders];

     setHeaders(newHeaders);

     setVisibleColumns((prevVisibleColumns) => {
       const updatedVisibleColumns: Record<string, boolean> = {};

       // Add all static header visibility
       staticHeaders.forEach((header) => {
         const key = header?.label?.replace(/\s+/g, '')?.toLowerCase();
         updatedVisibleColumns[key] =
           prevVisibleColumns[key] !== undefined
             ? prevVisibleColumns[key]
             : !defaultHiddenDropOffColumns?.includes(key);
       });

       // Add new bus header visibility - preserve existing state or default to true
       busHeaders.forEach((busHeader) => {
         const key = busHeader?.label?.replace(/\s+/g, '')?.toLowerCase();
         updatedVisibleColumns[key] = true;
       });

       return updatedVisibleColumns;
     });

     busHeaders?.forEach((bus) => {
       keyMapping[bus.key] = bus.key;
     });
   } else {
     setHeaders((prevHeaders) => prevHeaders?.filter((header) => !header.key));
     // Remove bus columns from visibleColumns when no buses
     setVisibleColumns((prev) => {
       const updated = { ...prev };
       Object.keys(updated)?.forEach((key) => {
         // Check if this key corresponds to a bus column by checking if it matches any previous bus initial
         const isStaticColumn = dropOffHeader?.some(
           (header) => header?.label?.replace(/\s+/g, '')?.toLowerCase() === key,
         );
         if (!isStaticColumn) {
           delete updated[key];
         }
       });
       return updated;
     });
   }
 }, [operationBus]);

  // update ealTimeBusPax values and busAllocations
  useEffect(() => {
    if (operationBus && busPax) {
      setRealTimeBusPax({
        totalPax: busPax?.totalPax ?? 0,
        totalAllocatedPax: busPax?.totalAllocatedPax ?? 0,
      });

      // Initialize bus-specific allocation counts
      const initialBusAllocs: { [operationStaffId: string]: number } = {};
      operationBus?.forEach((bus) => {
        initialBusAllocs[bus.operationStaffId] = bus.allocatedPax || 0;
      });
      setBusAllocations(initialBusAllocs);
    }
  }, [operationBus, busPax]);

  //calculate and update allocation stats based on changes
  const updateAllocationStats = useCallback(() => {
    // Calculate bus-specific allocations
    const newBusAllocations: { [operationStaffId: string]: number } = {};
    operationBus.forEach((bus) => {
      newBusAllocations[bus.operationStaffId] = 0;
    });

    // Calculate total allocated pax and bus-specific allocations
    let totalAllocatedPax = 0;

    Object.values(selectedBuses).forEach((booking) => {
      booking.allocations.forEach((allocation) => {
        if (!allocation.isDeleted) {
          const busId = allocation.operationStaffId.toString();
          const paxCount = allocation.splitPax;

          if (newBusAllocations[busId] !== undefined) {
            newBusAllocations[busId] += paxCount;
            totalAllocatedPax += paxCount;
          }
        }
      });
    });

    // Include original allocations that are not in selectedBuses
    bookings?.forEach((booking) => {
      // Skip if this booking is in our selectedBuses (already counted)
      // if (selectedBuses[booking.bookingId]) return;

      if (
        selectedBuses[
          `${booking?.bookingId}_${booking?.ZoneId}_${booking?.bookingProductTripId}`
        ]
      )
        return;

      // Add all original allocations from bookings not in selectedBuses
      booking.busPickupAllocation?.forEach((allocation) => {
        if (allocation.operationStaffId) {
          const busId = allocation.operationStaffId.toString();
          const paxCount = Number(allocation.paxNo || booking.allocPax);

          if (newBusAllocations[busId] !== undefined) {
            newBusAllocations[busId] += paxCount;
            totalAllocatedPax += paxCount;
          }
        }
      });
    });

    // Update the state with new allocations
    setBusAllocations(newBusAllocations);
    setRealTimeBusPax((prev) => ({
      ...prev,
      totalAllocatedPax,
    }));
  }, [selectedBuses, operationBus, bookings]);

  // Call this function whenever selectedBuses changes
  useEffect(() => {
    updateAllocationStats();
  }, [selectedBuses, updateAllocationStats]);

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
      setSelectedDateFromStore(date);
    }
  };

  const toggleColumn = (column: keyof IVisibleColumns) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [column]: !prev[column],
    }));
  };

  const onSetIsToggleColumns = () => {
    setIsToggleColumns(!isToggleColumns);
  };

  const ref = useClickOutside<HTMLDivElement>(() => {
    setIsToggleColumns(false);
  });

  // TODO: fix already allocated pax issue I can select check box after unselect checkbox
  const handleCheckboxChange = (
    bookingId: string | number,
    operationStaffId: string | number,
    busAllocationId: string | number | null,
    bookingProductTripPickupId: string | number,
    paxNo: string | number,
    oldOperationStaffId: string | number | null,
    zoneId: number,
    bookingProductTripId: string | number,
  ) => {
    const bookingIdStr = String(bookingId);
    const operationStaffIdStr = String(operationStaffId);
    const zoneIdStr = String(zoneId);
    const bookingProductTripIdStr = String(bookingProductTripId);
    const combinedKey = `${bookingIdStr}_${zoneIdStr}_${bookingProductTripIdStr}`;

    setSelectedBuses((prev) => {
      const currentBooking = prev[combinedKey];
      // Check if this is an original allocation
      const isOriginalAllocation = originalAllocations[combinedKey]?.some(
        (orig) => String(orig.operationStaffId) === operationStaffIdStr,
      );

      if (!currentBooking) {
        // First selection for this booking - set full pax count since it's the only allocation
        return {
          ...prev,
          [combinedKey]: {
            bookingProductTripPickupId,
            bookingId,
            paxNo,
            allocNo: Number(paxNo), // Set allocNo to full pax count
            zoneId,
            allocations: [
              {
                operationStaffId,
                busAllocationId,
                oldOperationStaffId,
                splitPax: Number(paxNo), // Set initial splitPax to full pax count
                isDeleted: false,
              },
            ],
          },
        };
      }

      // Check if this bus is already in allocations (active or deleted)
      const existingAllocationIndex = currentBooking.allocations.findIndex(
        (a) => String(a.operationStaffId) === operationStaffIdStr,
      );

      // when bus is already deleted(Unallocated)
      if (
        existingAllocationIndex !== -1 &&
        currentBooking.allocations[existingAllocationIndex].isDeleted
      ) {
        // This is a previously deleted allocation that should be reactivated
        const updatedAllocations = [...currentBooking.allocations];
        const activeAllocations = updatedAllocations.filter(
          (a, idx) => idx !== existingAllocationIndex && !a.isDeleted,
        );

        // If there are no other active allocations, give this one the full pax count
        const splitPax = activeAllocations.length === 0 ? Number(paxNo) : 0;

        updatedAllocations[existingAllocationIndex] = {
          ...updatedAllocations[existingAllocationIndex],
          isDeleted: false,
          splitPax,
        };

        return {
          ...prev,
          [combinedKey]: {
            ...currentBooking,
            allocations: updatedAllocations,
            allocNo: activeAllocations.reduce(
              (sum, a) => sum + (a.splitPax || 0),
              splitPax,
            ),
          },
        };
      }

      // When bus is already active(allocated)
      if (
        existingAllocationIndex !== -1 &&
        !currentBooking.allocations[existingAllocationIndex].isDeleted
      ) {
        // Bus is already selected - handle unselection
        if (isOriginalAllocation) {
          // For originally allocated buses, mark for deletion instead of removing
          const updatedAllocations = currentBooking.allocations.map(
            (alloc, index) =>
              index === existingAllocationIndex
                ? { ...alloc, isDeleted: true }
                : alloc,
          );

          // Find active allocations (not marked for deletion)
          const activeAllocations = updatedAllocations.filter(
            (a) => !a.isDeleted,
          );

          // Calculate total allocated pax from active allocations
          let totalAllocated = activeAllocations.reduce(
            (sum, alloc) => sum + (alloc.splitPax || 0),
            0,
          );

          // If only one allocation remains active, give it the full pax count
          if (activeAllocations.length === 1) {
            const remainingAllocationIndex = updatedAllocations.findIndex(
              (a) => !a.isDeleted,
            );
            if (remainingAllocationIndex !== -1) {
              updatedAllocations[remainingAllocationIndex].splitPax =
                Number(paxNo);
              totalAllocated = Number(paxNo);
            }
          }

          // Remove related error if exists
          setPaxErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            delete updatedErrors[`${combinedKey}_${operationStaffIdStr}`];
            return updatedErrors;
          });

          return {
            ...prev,
            [combinedKey]: {
              ...currentBooking,
              allocations: updatedAllocations,
              allocNo: totalAllocated,
            },
          };
        } else {
          // For new allocations, remove them entirely
          const newAllocations = currentBooking.allocations.filter(
            (_, index) => index !== existingAllocationIndex,
          );

          if (newAllocations.length === 0) {
            const newSelected = { ...prev };
            delete newSelected[combinedKey];
            return newSelected;
          }

          // Calculate total allocated pax from remaining allocations
          let totalAllocated = newAllocations.reduce(
            (sum, alloc) => sum + (alloc.splitPax || 0),
            0,
          );

          // If only one allocation remains, give it the full pax count
          if (newAllocations.length === 1) {
            newAllocations[0].splitPax = Number(paxNo);
            totalAllocated = Number(paxNo);
          }

          // Remove related error if exists
          setPaxErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            delete updatedErrors[`${combinedKey}_${operationStaffIdStr}`];
            return updatedErrors;
          });

          return {
            ...prev,
            [combinedKey]: {
              ...currentBooking,
              allocations: newAllocations,
              allocNo: totalAllocated,
            },
          };
        }
      }

      // Add new bus allocation
      const activeAllocations = currentBooking.allocations.filter(
        (a) => !a.isDeleted,
      );
      const splitPax = activeAllocations.length === 0 ? Number(paxNo) : 0;

      const newAllocation = {
        operationStaffId,
        busAllocationId,
        oldOperationStaffId: null,
        splitPax,
        isDeleted: false,
      };

      const newAllocations = [...currentBooking.allocations, newAllocation];
      const totalAllocated = newAllocations
        .filter((a) => !a.isDeleted)
        .reduce((sum, alloc) => sum + (alloc.splitPax || 0), 0);

      return {
        ...prev,
        [combinedKey]: {
          ...currentBooking,
          allocations: newAllocations,
          allocNo: totalAllocated,
        },
      };
    });
  };

  const handlePaxCountChange = (
    bookingId: string | number,
    operationStaffId: string | number,
    newPaxCount: string,
    zoneId: number,
    bookingProductTripId: string | number,
  ) => {
    const bookingIdStr = String(bookingId);
    const operationStaffIdStr = String(operationStaffId);
    const zoneIdStr = String(zoneId);
    const bookingProductTripIdStr = String(bookingProductTripId);
    const combinedKey = `${bookingIdStr}_${zoneIdStr}_${bookingProductTripIdStr}`;
    const numPaxCount = parseInt(newPaxCount) || 0;
    const key = `${combinedKey}_${operationStaffIdStr}`;

    setSelectedBuses((prev) => {
      const currentBooking = prev[combinedKey];
      if (!currentBooking) return prev;

      const totalPax = Number(currentBooking.paxNo);

      // Create a copy of the allocations
      const updatedAllocations = [...currentBooking.allocations];

      // Find the index of the current allocation being modified
      const currentAllocationIndex = updatedAllocations.findIndex(
        (a) =>
          String(a.operationStaffId) === operationStaffIdStr && !a.isDeleted,
      );

      if (currentAllocationIndex === -1) return prev;

      // Update only the current allocation with the new pax count
      updatedAllocations[currentAllocationIndex] = {
        ...updatedAllocations[currentAllocationIndex],
        splitPax: numPaxCount,
      };

      // Recalculate total pax
      const totalAllocated = updatedAllocations.reduce((sum, alloc) => {
        return !alloc.isDeleted
          ? sum + (parseInt(alloc.splitPax as any) || 0)
          : sum;
      }, 0);

      // Update error state
      setPaxErrors((prevErrors) => {
        const updated = { ...prevErrors };

        // Clear all errors for this booking
        Object.keys(updated).forEach((k) => {
          if (k.startsWith(`${combinedKey}_`)) {
            delete updated[k];
          }
        });

        // If over-allocated, set error only on the current field
        if (totalAllocated > totalPax) {
          updated[key] = true;
        }

        return updated;
      });

      return {
        ...prev,
        [combinedKey]: {
          ...currentBooking,
          allocations: updatedAllocations,
          allocNo: totalAllocated,
        },
      };
    });
  };

  // update  the toggleEditMode function to reset values when canceling edits
  const toggleEditMode = () => {
    if (isEditMode) {
      const originalBusAllocs: { [operationStaffId: string]: number } = {};
      operationBus?.forEach((bus) => {
        originalBusAllocs[bus.operationStaffId] = bus.allocatedPax || 0;
      });
      setBusAllocations(originalBusAllocs);

      // Restore original busPax count
      setRealTimeBusPax({
        totalPax: busPax?.totalPax ?? 0,
        totalAllocatedPax: busPax?.totalAllocatedPax ?? 0,
      });

      // Reset selectedBuses to original state
      const initialSelectedBuses: { [key: string]: ISelectedBusData } = {};
      const initialOriginalAllocations: {
        [key: string]: IOriginalAllocation[];
      } = {};

      bookings?.forEach((booking) => {
        const bookingIdStr = String(booking.bookingId);
        const zoneIdStr = String(booking.ZoneId);
        const bookingProductTripIdStr = String(booking.bookingProductTripId);
        const combinedKey = `${bookingIdStr}_${zoneIdStr}_${bookingProductTripIdStr}`;

        if (!initialOriginalAllocations[combinedKey]) {
          initialOriginalAllocations[combinedKey] = [];
        }

        if (!initialSelectedBuses[combinedKey]) {
          initialSelectedBuses[combinedKey] = {
            bookingProductTripPickupId: booking.bookingProductTripDropOffId,
            bookingId: booking.bookingId,
            paxNo: booking.paxNo,
            allocNo: booking.allocPax,
            zoneId: booking.ZoneId,
            allocations: [],
          };
        }

        booking.busPickupAllocation?.forEach((allocation) => {
          if (allocation.operationStaffId) {
            initialOriginalAllocations[combinedKey].push({
              operationStaffId: allocation.operationStaffId,
              busAllocationId: allocation?.busAllocationId,
              paxNo: Number(allocation.paxNo || booking.allocPax),
            });

            initialSelectedBuses[combinedKey].allocations.push({
              operationStaffId: allocation.operationStaffId,
              busAllocationId: allocation?.busAllocationId,
              oldOperationStaffId: allocation.operationStaffId,
              splitPax: Number(allocation.paxNo || booking.allocPax),
              isDeleted: false,
            });
          }
        });

        if (initialSelectedBuses[combinedKey].allocations.length === 0) {
          delete initialSelectedBuses[combinedKey];
        }
      });

      setOriginalAllocations(initialOriginalAllocations);
      setSelectedBuses(initialSelectedBuses);
    }

    setIsEditMode((prev) => !prev);
  };

  // Handle cancel button click
  const handleCancelEdit = () => {
    // Reset to original state of allocations


    // Reset selected buses
    const initialSelectedBuses: { [key: string]: ISelectedBusData } = {};
    const initialOriginalAllocations: { [key: string]: IOriginalAllocation[] } =
      {};

    bookings?.forEach((booking) => {
      const bookingIdStr = String(booking.bookingId);
      const zoneIdStr = String(booking.ZoneId);
      const bookingProductTripIdStr = String(booking.bookingProductTripId);
      const combinedKey = `${bookingIdStr}_${zoneIdStr}_${bookingProductTripIdStr}`;
      if (!initialOriginalAllocations[combinedKey]) {
        initialOriginalAllocations[combinedKey] = [];
      }

      if (!initialSelectedBuses[combinedKey]) {
        initialSelectedBuses[combinedKey] = {
          bookingProductTripPickupId: booking.bookingProductTripDropOffId,
          bookingId: booking.bookingId,
          paxNo: booking.paxNo,
          allocNo: booking.allocPax,
          zoneId:booking.ZoneId,
          allocations: [],
        };
      }

      booking.busPickupAllocation?.forEach((allocation) => {
        if (allocation.operationStaffId) {
          initialOriginalAllocations[combinedKey].push({
            operationStaffId: allocation.operationStaffId,
            busAllocationId: allocation?.busAllocationId,
            paxNo: Number(allocation.paxNo || booking.allocPax),
          });

          initialSelectedBuses[combinedKey].allocations.push({
            operationStaffId: allocation.operationStaffId,
            busAllocationId: allocation?.busAllocationId,
            oldOperationStaffId: allocation.operationStaffId,
            splitPax: Number(allocation.paxNo || booking.allocPax),
            isDeleted: false,
          });
        }
      });

      if (initialSelectedBuses[combinedKey].allocations.length === 0) {
        delete initialSelectedBuses[combinedKey];
      }
    });

    setOriginalAllocations(initialOriginalAllocations);
    setSelectedBuses(initialSelectedBuses);
    setPaxErrors({});
    setIsEditMode(false);
  };

  const allocatedBus = async () => {
    try {
      if (Object.keys(selectedBuses).length === 0) return;
      const payload = Object?.entries(selectedBuses)?.flatMap(
        ([bookingId, booking]) => {
          const original = originalAllocations[bookingId] || [];
          const current = booking.allocations;

          return (
            current
              .filter((allocation) => {
                const originalAlloc = original.find(
                  (orig) =>
                    String(orig.operationStaffId) ===
                    String(allocation.operationStaffId),
                );

                return (
                  allocation.isDeleted ||
                  !originalAlloc ||
                  originalAlloc.paxNo !== allocation.splitPax
                );
              })
              // Filter out allocations with splitPax that is undefined, null, or 0
              .filter((allocation) => {
                // Allow deleted allocations through regardless of paxNo
                if (allocation.isDeleted) return true;

                // Filter out allocations with invalid pax counts
                return (
                  allocation.splitPax !== undefined &&
                  allocation.splitPax !== null &&
                  allocation.splitPax > 0
                );
              })
              .map((allocation) => ({
                bookingProductTripPickupId: Number(
                  booking.bookingProductTripPickupId,
                ),
                bookingId: Number(booking.bookingId),
                operationStaffId: Number(allocation.operationStaffId),
                paxNo: allocation.splitPax,
                ...(allocation.busAllocationId && {
                  busAllocationId: Number(allocation.busAllocationId),
                }),
                ...(allocation.oldOperationStaffId && {
                  oldOperationStaffId: Number(allocation.oldOperationStaffId),
                }),
                isDeleted: allocation.isDeleted || false,
              }))
          );
        },
      );

      console.log('payload value is here ', payload);
      if (payload.length > 0) {
        setLoading(true);
        const response =
          await driverAllocationAPI.addDriverDropOffAllocation(payload);
        console.log('Response:', response?.data);
        showOrReplaceToast(
          'dropOff',
          translate('busAllocation.successMessage.allocationSave'),
          'success',
        );
        await getPickupAllocationDetails(
          selectedDate,
          selectedLocation.OwnerId,
        );
      } else {
        console.log('No changes detected in allocations'); //TODO: I will remove this in my next PR
      }
    } catch (error) {
      console.error('Error fetching allocation details:', error);
    } finally {
      setLoading(false);
      setIsEditMode(false);
    }
  };

  return (
    <div className="bg-background px-6">
      <div className=" bg-background">
        <CalendarHeader />
      </div>
      <div className="text-start flex justify-between">
        <div>
          <h1 className="text-lg font-semibold text-textColorGray">
            {translate('dropOffScreen.busAllocationHeading')}
          </h1>
        </div>
        <div>
          <AllocationsPax<IPaxDriverAllocation>
            message={translate('busAllocation.incorrectAllocation')}
            heading={translate('busAllocation.incorrectAllocation')}
            data={unallocatedPax ? unallocatedPax : []}
            totalPax={busPax?.totalPax}
            totalAllocatedPax={realTimeBusPax['totalAllocatedPax']}
          />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-background mt-2 rounded-lg flex items-end justify-between">
        <div className="flex">
          <div className="text-start mr-4">
            <label className="text-textColor">
              {translate('common.location')}
            </label>
            <ReusableSelectLocation<ILocation>
              options={(locationList || []) as ILocation[]}
              className="w-full border border-customBorder-border rounded p-2 text-sm  bg-sidebar-background text-text-primary"
              getOptionLabel={(option: ILocation) => `${option.locationName}`}
              getOptionValue={(option: ILocation) => option.OwnerId}
              onChange={handleLocationChange}
              placeholder="Select a locations"
              value={selectedLocationId}
            />
          </div>
          <div className="text-start mr-4 z-20">
            <label className="text-textColor">{translate('common.date')}</label>
            <div className="  bg-sidebar-background text-text-primary flex items-center h-[38px] p-2 rounded border border-customBorder-border">
              <DateSelector
                onDateChange={handleDateChange}
                initialDate={selectedDate}
                showArrows={true}
              />
            </div>
          </div>
        </div>
        <div
          className={`w-[18%] 2xl:w-[12%] text-gray-600 text-sm font-medium flex items-center justify-between ${bgColor} h-[38px] w-5/6 2xl:w-1/2`}
        >
          <span className="text-text-primary font-normal ml-4 text-[14px]">
            {translate('common.bus')}
          </span>
          <span className="text-text-primary font-normal mr-4 text-[14px]">
            <span className="text-textColor">
              {realTimeBusPax['totalAllocatedPax']}
            </span>
            /{busPax['totalPax']}
          </span>
        </div>
      </div>

      {/* Cards */}
      <div className="sticky top-0  grid grid-cols-6 gap-4 mt-4">
        {operationBus?.map((bus) => (
          <>
            <BusCard
              busName={bus.busName}
              capacity={bus.capacity}
              allocatedPax={busAllocations[bus.operationStaffId]}
              bgColor={`${bgColor}`}
              balloon={bus?.balloon}
              initials={bus?.initials}
            />
          </>
        ))}
      </div>

      {/* Table Controls */}
      <div
        className={`sticky z-10 bg-background mt-4 pt-2 ${operationBus?.length > 0 ? 'top-[115px]' : 'top-0'}`}
      >
        <div
          className={`flex justify-end items-center p-2 rounded-lg bg-background`}
        >
          <div ref={ref} className="relative">
            <ReusableButton
              variant="secondary"
              children={'Select Columns'}
              icon={
                <div>
                  <div className="block dark:hidden">
                    <AccordianDownIcon />
                  </div>
                  <div className="hidden dark:block">
                    <AccordianDownIconDark />
                  </div>
                </div>
              }
              textColor={'text-textColor'}
              onClick={onSetIsToggleColumns}
              bgColor={'bg-backgroundColor-darkThemeColor'}
            />
            {isToggleColumns && (
              <div className="absolute right-0 bg-background border -mt-2 border-slate-100 shadow-lg rounded z-10 overflow-y-auto max-h-64">
                {(Object.keys(visibleColumns) as (keyof IVisibleColumns)[]).map(
                  (column) => {
                    // This lookup is required to get the correct label and type
                    const header = headers.find(
                      (h) =>
                        h.label.replace(/\s+/g, '')?.toLowerCase() === column,
                    );
                    const isDynamicDriverColumn = header?.key;
                    const displayLabel = header ? header.label : column;

                    return (
                      <label
                        key={column}
                        className="flex items-center px-4 py-2 space-x-1"
                      >
                        <input
                          type="checkbox"
                          checked={visibleColumns[column]}
                          onChange={() => toggleColumn(column)}
                        />
                        <span
                          className={`text-text-secondary ${
                            isDynamicDriverColumn ? 'uppercase' : 'capitalize'
                          }`}
                        >
                          {displayLabel}
                        </span>
                      </label>
                    );
                  },
                )}
              </div>
            )}
          </div>
          <div className="flex space-x-2">
            {isEditMode ? (
              <>
                <ReusableIconButton
                  icon={
                    <CrossIcon
                      stroke={'#FF5733'}
                      className="w-6 h-6 text-red-500"
                    />
                  }
                  onClick={handleCancelEdit}
                  className="border-red-500"
                />

                <ReusableIconButton
                  icon={loading ? <Loader /> : <CheckIcon stroke={'green'} />}
                  onClick={() => allocatedBus()}
                  className="border-green"
                />
              </>
            ) : (
              <ReusableButton
                variant="secondary"
                children={translate('common.edit')}
                icon={
                  <div>
                    <div className="block dark:hidden">
                      <EditIcon />
                    </div>
                    <div className="hidden dark:block">
                      <EditIconDark />
                    </div>
                  </div>
                }
                bgColor={'bg-backgroundColor-darkThemeColor'}
                textColor={'text-textColor'}
                onClick={toggleEditMode}
                isDisabled={bookings?.length === 0}
              />
            )}
          </div>
        </div>
      </div>
      {/* Table */}
      <div className={`${bgColor} overflow-x-auto`}>
        <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
          <table className="min-w-full border-collapse border border-customBorder-tableBorder shadow-lg text-left">
            <thead className="bg-[#004E9B] text-white sticky top-0">
              <tr className="text-sm">
                {headers
                  ?.filter((header) => {
                    const filterKey = header.label
                      .replace(/\s+/g, '')
                      .toLowerCase();
                    return visibleColumns[filterKey] === true;
                  })
                  .map((header, index) => {
                    return (
                      <th
                        key={index}
                        className="border border-slate-100 p-3 text-sm"
                        title={header.title}
                      >
                        {header.label}
                      </th>
                    );
                  })}
              </tr>
            </thead>
            <tbody>
              {isPageLoading ? (
                <tr>
                  <td
                    colSpan={headers?.length || 1}
                    className="text-center p-4 h-[200px] text-lg font-semibold"
                  >
                    <div className="inline-grid">
                      <Loader width={50} height={50} />
                    </div>
                  </td>
                </tr>
              ) : bookings?.length === 0 ? (
                <tr>
                  <td
                    colSpan={headers?.length || 1}
                    className="text-center p-4 h-[200px] text-lg font-semibold text-text-primary"
                  >
                    {translate('flightAllocation.noData')}
                  </td>
                </tr>
              ) : (
                bookings?.map((row, index) => {
                  const bookingIdStr = String(row.bookingId);
                  const zoneIdStr = String(row.ZoneId);
                  const bookingProductTripIdStr = String(
                    row.bookingProductTripId,
                  );
                  const combinedKey = `${bookingIdStr}_${zoneIdStr}_${bookingProductTripIdStr}`;
                  const highlight =
                    selectedBuses[combinedKey] &&
                    selectedBuses[combinedKey]?.allocations?.filter(
                      (a) => !a.isDeleted,
                    )?.length > 0;

                  return (
                    <tr
                      key={index}
                      className={`${!highlight ? 'bg-backgroundColor-tableBgColor text-black ' : index % 2 === 0 ? 'bg-backgroundColor-white text-text-primary' : 'bg-gray-100 text-text-primary'} `}
                    >
                      {headers?.filter((header) => {
                       const filterKey = header.label.replace(/\s+/g, '').toLowerCase();
                         return visibleColumns[filterKey] === true;
                       })?.map((header, headerIndex) => {
                        const filterKey = header.label
                          ?.replace(/\s+/g, '')
                          ?.toLowerCase();

                        const rowKey = keyMapping[filterKey];
                        const bus = operationBus?.find(
                          (b) =>
                            b?.operationStaffId?.toString() === header?.key,
                        );
                        if (bus) {
                          return (
                            <td
                              key={headerIndex}
                              className="border-gray-300 p-3 text-center min-w-8"
                            >
                              <div className="flex items-center justify-center">
                                <input
                                  type="checkbox"
                                  disabled={!isEditMode}
                                  className="w-5 h-5 border-gray-300 rounded focus:ring-blue-500 mr-2"
                                  checked={
                                    selectedBuses[
                                      combinedKey
                                    ]?.allocations.some(
                                      (a) =>
                                        String(a.operationStaffId) ===
                                          String(bus.operationStaffId) &&
                                        !a.isDeleted,
                                    ) ||
                                    (!selectedBuses[combinedKey] &&
                                      row.busPickupAllocation?.some(
                                        (allocation) =>
                                          String(
                                            allocation.operationStaffId,
                                          ) === String(bus.operationStaffId),
                                      ))
                                  }
                                  onChange={() => {
                                    const existingAllocation =
                                      row.busPickupAllocation?.find(
                                        (allocation) =>
                                          String(
                                            allocation.operationStaffId,
                                          ) === String(bus.operationStaffId),
                                      );

                                    handleCheckboxChange(
                                      row.bookingId,
                                      bus.operationStaffId,
                                      existingAllocation?.busAllocationId ||
                                        null,
                                      row.bookingProductTripDropOffId,
                                      row.paxNo,
                                      existingAllocation?.operationStaffId ||
                                        null,
                                      row.ZoneId,
                                      row.bookingProductTripId,
                                    );
                                  }}
                                />

                                {isEditMode &&
                                  selectedBuses[
                                    combinedKey
                                  ]?.allocations.filter((a) => !a.isDeleted)
                                    .length > 1 &&
                                  selectedBuses[combinedKey]?.allocations.some(
                                    (a) =>
                                      String(a.operationStaffId) ===
                                        String(bus.operationStaffId) &&
                                      !a.isDeleted,
                                  ) && (
                                    <input
                                      type="number"
                                      min="0"
                                      max={Number(row.allocPax)}
                                      value={
                                        selectedBuses[
                                          combinedKey
                                        ]?.allocations.find(
                                          (a) =>
                                            String(a.operationStaffId) ===
                                              String(bus.operationStaffId) &&
                                            !a.isDeleted,
                                        )?.splitPax || ''
                                      }
                                      onChange={(e) =>
                                        handlePaxCountChange(
                                          row.bookingId,
                                          bus.operationStaffId,
                                          e.target.value,
                                          row.ZoneId,
                                          row.bookingProductTripId,
                                        )
                                      }
                                      className={`w-16 h-8 border rounded px-2 text-black`}
                                      placeholder="Pax"
                                    />
                                  )}
                              </div>
                            </td>
                          );
                        }

                        if (!visibleColumns[filterKey] || !rowKey) return null;
                        const rowData = row[rowKey as keyof typeof row];

                        if (filterKey === allocation.ALLOCATED) {
                          const paxAllocatedCount =
                            selectedBuses[combinedKey] &&
                            selectedBuses[combinedKey]?.allocations?.filter(
                              (a) => !a.isDeleted,
                            )?.length > 0
                              ? selectedBuses[combinedKey]?.allocNo
                              : 0;

                          return (
                            <td
                              key={headerIndex}
                              className="border-gray-300 p-3 min-w-8"
                            >
                              {paxAllocatedCount}
                            </td>
                          );
                        }

                        const shouldTruncate =
                          truncateColumn.includes(filterKey);
                        const displayValue =
                          filterKey === allocation.LUGGAGE
                            ? rowData === true
                              ? translate('common.Y')
                              : rowData === false
                                ? ''
                                : rowData
                            : rowData;
                        return (
                          <td
                            key={headerIndex}
                            className={`border-gray-300 p-3 
                          ${filterKey === allocation.AGENT ? ' min-w-48' : filterKey === allocation.NAME ? 'min-w-32' : ' min-w-8'}
                          ${shouldTruncate ? 'truncate max-w-xs cursor-pointer' : ''}`}
                            title={
                              shouldTruncate && typeof displayValue === 'string'
                                ? displayValue
                                : undefined
                            }
                          >
                            {shouldTruncate &&
                            typeof displayValue === 'string' &&
                            displayValue.length > 20
                              ? `${displayValue.substring(0, 20)}...`
                              : Array.isArray(displayValue)
                                ? JSON.stringify(displayValue) // Convert array to string for rendering
                                : displayValue}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DropOffDesktop;
